import { AddModeratorOutlined, RemoveCircleOutline, RemoveModeratorOutlined, Search } from "@mui/icons-material";
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer as MuiTableContainer,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  dialogClasses,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import AppDialog from "components/common/AppDialog";
import { AdvancedConfirmDialog, NoData, StyledTableHead, TableContainer } from "components";
import { LangConstant, SystemConstant } from "const";
import { useChannelMembers, useRemoveChannelMember, useUpdateChannelMemberRole } from "hooks";
import { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import AddChannelMemberDialog from "./AddChannelMemberDialog";

const VIEW_MODE = {
  addMember: "addMember",
  deleteMember: "deleteMember",
  changeAdminRole: "changeAdminRole",
};

const UpdateChannelDialog = ({ open, channel, onClose }) => {
  const { t: getLabel } = useTranslation();
  const labels = getLabel(LangConstant.OBJ_UPDATE_CHANNEL_DIALOG, { returnObjects: true });

  const tableContainerRef = useRef(null);

  const { members, totalMembers, totalAdmins, handleSearch, handleLoadMore, handleRefreshQuery } = useChannelMembers({
    groupId: channel?.id,
  });
  const { removeChannelMember } = useRemoveChannelMember();
  const { grantAdmin, revokeAdmin } = useUpdateChannelMemberRole();

  const [selectedMember, setSelectedMember] = useState(null);
  const [viewMode, setViewMode] = useState(null);

  const columns = [
    { label: labels.colFullName, align: "left" },
    { label: labels.colUnit, align: "left", width: "15%" },
    { label: labels.colDepartment, align: "left", width: "18%" },
    { label: labels.colRole, align: "left", width: "15%" },
    { label: labels.colAction, align: "center", width: "10%" },
  ];

  const handleScroll = useCallback(() => {
    const el = tableContainerRef.current;
    if (!el) return;
    const isAtBottom = el.scrollHeight - el.scrollTop <= el.clientHeight + 8;
    if (isAtBottom) handleLoadMore();
  }, [handleLoadMore]);

  const handleSearchMember = event => {
    handleSearch(event.target.value);
  };

  const handleRemoveMember = member => () => {
    setSelectedMember(member);
    setViewMode(VIEW_MODE.deleteMember);
  };

  const handleChangeAdminRole = member => () => {
    setSelectedMember(member);
    setViewMode(VIEW_MODE.changeAdminRole);
  };

  const handleAddMember = () => {
    setViewMode(VIEW_MODE.addMember);
  };

  const handleReset = () => {
    setViewMode(null);
    setSelectedMember(null);
  };

  const handleConfirmRemoveMember = () => {
    removeChannelMember({
      groupId: channel?.id,
      memberId: selectedMember.id,
      onResetMembers: handleRefreshQuery,
      onSuccess: handleReset,
    });
  };

  const handleConfirmChangeAdminRole = () => {
    const action = selectedMember.isAdmin ? revokeAdmin : grantAdmin;
    action({
      groupId: channel?.id,
      memberId: selectedMember.id,
      onResetMembers: handleRefreshQuery,
      onSuccess: handleReset,
    });
  };

  return (
    <StyledAppDialog open={open} title={labels.title} onClose={onClose}>
      <Typography fontSize={16} fontWeight={700}>
        {labels.totalMembers}:
        <Box component="span" fontWeight={400}>
          {totalMembers}
        </Box>
      </Typography>

      <TextField
        fullWidth
        size="small"
        label={labels.searchLabel}
        placeholder={labels.searchPlaceholder}
        InputLabelProps={{ shrink: true }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <Search fontSize="small" color="action" />
            </InputAdornment>
          ),
        }}
        onChange={handleSearchMember}
      />

      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography fontSize={15} fontWeight={700}>
          {labels.userListTitle}
        </Typography>
        <Button variant="contained" onClick={handleAddMember}>
          {labels.addMember}
        </Button>
      </Stack>

      <TableContainer ref={tableContainerRef} onScroll={handleScroll}>
        <Table stickyHeader size="small">
          <StyledTableHead columns={columns} />
          <TableBody>
            {members.map(member => {
              const isDisableRoleBtn = member.isAdmin && totalAdmins <= 1;
              const isDisableDeleteBtn = totalMembers <= SystemConstant.MIN_CHANNEL_MEMBER_LENGTH || isDisableRoleBtn;
              return (
                <TableRow key={member.id} hover sx={{ backgroundColor: member.isAdmin ? "#EBF6FD" : "inherit" }}>
                  <TableCell align="left">{member.name}</TableCell>
                  <TableCell align="left">{member.unitName || "--"}</TableCell>
                  <TableCell align="left">{member.departmentName || "--"}</TableCell>
                  <TableCell align="left">{member.isAdmin ? labels.roleAdmin : labels.roleMember}</TableCell>
                  <TableCell align="center">
                    <Tooltip
                      title={
                        isDisableRoleBtn
                          ? getLabel("TXT_REVOKE_CHANNEL_ADMIN_DISABLED")
                          : getLabel(member.isAdmin ? "TXT_REVOKE_CHANNEL_ADMIN_BTN" : "TXT_GRANT_CHANNEL_ADMIN_BTN")
                      }
                    >
                      <span>
                        <IconButton
                          size="small"
                          color="info"
                          disabled={isDisableRoleBtn}
                          onClick={handleChangeAdminRole(member)}
                        >
                          {member.isAdmin ? <RemoveModeratorOutlined /> : <AddModeratorOutlined />}
                        </IconButton>
                      </span>
                    </Tooltip>

                    <Tooltip
                      title={
                        isDisableDeleteBtn
                          ? member.isAdmin && totalAdmins <= 1
                            ? getLabel("TXT_REVOKE_CHANNEL_ADMIN_DISABLED")
                            : getLabel("TXT_REMOVE_CHANNEL_MEMBER_DISABLED_MIN")
                          : getLabel("TXT_REMOVE_CHANNEL_MEMBER_BTN")
                      }
                    >
                      <span>
                        <IconButton
                          size="small"
                          color="error"
                          disabled={isDisableDeleteBtn}
                          onClick={handleRemoveMember(member)}
                        >
                          <RemoveCircleOutline fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>

        {members.length === 0 && <NoData />}
      </TableContainer>

      {viewMode === VIEW_MODE.changeAdminRole && selectedMember && (
        <AdvancedConfirmDialog
          open
          title={getLabel(selectedMember.isAdmin ? "TXT_REVOKE_CHANNEL_ADMIN_TITLE" : "TXT_ADD_CHANNEL_ADMIN_TITLE")}
          content={getLabel(
            selectedMember.isAdmin ? "TXT_REVOKE_CHANNEL_ADMIN_CONTENT" : "TXT_ADD_CHANNEL_ADMIN_CONTENT",
          )}
          submitProps={{ submitText: getLabel("L_CONFIRM_BUTTON"), onClick: handleConfirmChangeAdminRole }}
          cancelProps={{ onClick: handleReset }}
        />
      )}

      {viewMode === VIEW_MODE.deleteMember && selectedMember && (
        <AdvancedConfirmDialog
          open
          title={getLabel("TXT_DELETE_CHANNEL_MEMBER_TITLE")}
          content={getLabel("TXT_DELETE_CHANNEL_MEMBER_CONTENT")}
          submitProps={{ submitText: getLabel("L_CONFIRM_BUTTON"), onClick: handleConfirmRemoveMember }}
          cancelProps={{ onClick: handleReset }}
        />
      )}

      {viewMode === VIEW_MODE.addMember && (
        <AddChannelMemberDialog
          open
          groupId={channel?.id}
          onClose={handleReset}
          onRefreshMemberList={handleRefreshQuery}
        />
      )}
    </StyledAppDialog>
  );
};

export default UpdateChannelDialog;

const StyledAppDialog = styled(AppDialog)({
  [`& .${dialogClasses.paper}`]: {
    width: 1200,
    height: 615,
  },
});
